import { Component, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { ActivatedRoute, Data, Router } from "@angular/router";
import {
  CaseFinancingLoanData,
  LoanData,
  LoanDataSummary,
} from "../../loan/domain/models/loan.model";
import { combineLatest } from "rxjs";
import {
  FilterParameter,
  ProductCode,
  AccountFilters,
  AccountQueryParams,
  EntityExData,
  NeedsAttentionHelper,
  EntityHelper,
  NeedsAttentionModel,
  CompanyBrandingHelper,
  BrandingService,
  CompanyBranding,
  CustomerLoanStatus,
  QueryParamsService,
  SsoAccountData,
  SsoAccountFacade,
  SsoAccountCheckoutState,
  IdentityVerificationAction,
  LoanHelper,
} from "common";
import { MatDialog } from "@angular/material/dialog";
import { LoanFilterService } from "../../loan/infrastructure/loan-filter.service";
import * as _ from "lodash";
import { AccountListFilterDialogComponent } from "./account-list-filter-dialog/account-list-filter-dialog.component";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { AccountSearchInputService } from "../account-search-input/account-search-input.service";
import { LoanFacade } from "../../loan/domain/+state/loan.facade";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: "ifp-account-list",
    templateUrl: "./account-list.component.html",
    styleUrls: ["./account-list.component.scss"],
    standalone: false
})
export class AccountListComponent implements OnInit {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  hidePageSize: boolean;
  loans: (LoanData & CaseFinancingLoanData)[];
  filters: AccountFilters;
  readonly CustomerLoanStatus = CustomerLoanStatus;
  readonly IdentityVerificationAction = IdentityVerificationAction;
  currentAccount: SsoAccountData;
  checkoutState: SsoAccountCheckoutState;
  entity: EntityExData;
  form: UntypedFormGroup;
  showAccountsFilter = false;
  needsAttentionModel: NeedsAttentionModel;

  private displayedColumns = [
    { columnName: "favorite", showForProductCode: undefined },
    { columnName: "createdOn", showForProductCode: undefined },
    { columnName: "description", showForProductCode: undefined },
    { columnName: "status", showForProductCode: undefined },
    { columnName: "availableFunds", showForProductCode: undefined },
    { columnName: "creditLimit", showForProductCode: undefined },
    { columnName: "outstandingBalance", showForProductCode: undefined },
    { columnName: "upcomingPayment", showForProductCode: undefined },
    { columnName: "upcomingPaymentDate", showForProductCode: undefined },
    { columnName: "policyNumber", showForProductCode: ProductCode.Law },
    { columnName: "policyLimit", showForProductCode: ProductCode.Law },
    { columnName: "externalId", showForProductCode: ProductCode.Law },
    { columnName: "loanNumber", showForProductCode: undefined },
    { columnName: "more", showForProductCode: undefined },
  ];

  activeFilters: FilterParameter[] = [];
  hoverLoanId: number;
  productCode: ProductCode;
  excludedProductCode: ProductCode;
  productCodes = ProductCode;
  totalResultCount: number;
  hasDrawAccess = (loan: LoanData & CaseFinancingLoanData) => loan?.isDrawVisible
  hasPayAccess = (loan: LoanData) => loan?.isPayVisible

  queryParams: AccountQueryParams;

  constructor(
    public loanFacade: LoanFacade,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private loanFilterService: LoanFilterService,
    private formBuilder: UntypedFormBuilder,
    private accountSearchInputService: AccountSearchInputService,
    private queryParamsService: QueryParamsService,
    private brandingService: BrandingService,
    private ssoAccountFacade: SsoAccountFacade,
  ) {
    this.form = this.formBuilder.group({
      search: "",
    });
  }

  ngOnInit(): void {
    combineLatest([this.route.data, this.brandingService.getBranding(), this.ssoAccountFacade.getSsoAccountCheckoutState$()])
    .pipe(untilDestroyed(this)).subscribe((combinedResult: Array<Data | CompanyBranding | SsoAccountCheckoutState>) => {
      const res = combinedResult[0] as Data;
      this.productCode = res.productCode;
      this.excludedProductCode = res.excludedProductCode;
      this.currentAccount = res.currentAccount;
      this.entity = res.entity;
      this.checkoutState = combinedResult[2] as SsoAccountCheckoutState;

      const branding = combinedResult[1] as CompanyBranding;
      const advisorPhone = CompanyBrandingHelper.getAdvisorPhone(branding, res.entity?.advisorPhone);

      this.needsAttentionModel = {
        isApplicable: res.entity?.isDrawSuspended,
        messageInput: EntityHelper.getNeedsAttentionMessage({...res.entity, advisorPhone: advisorPhone})
      };
      this.setLoanData(res.loanSummaryData);
    });

    this.route.queryParams.pipe(untilDestroyed(this)).subscribe((it) => {
      this.queryParams = new AccountQueryParams(
        this.queryParamsService.init(it)
      );
      this.paginator.pageSize = this.queryParams.limit;
      this.paginator.pageIndex = this.queryParams.skip / this.queryParams.limit;
      this.activeFilters = this.loanFilterService.addFilterParameters(
        this.queryParams,
        this.productCode,
        this.excludedProductCode
      );
      this.filters = new AccountFilters(this.queryParams);
      if (!this.filters?.productCode)
        this.filters.productCode = this.productCode;

      if (!this.filters?.excludedProductCode)
        this.filters.excludedProductCode = this.excludedProductCode

      if(!this.filters.entityId)
        this.filters.entityId = this.entity.id
    });
  }

  setHoverLoan(loanId: number) {
    this.hoverLoanId = loanId;
  }

  setPage() {
    this.filter();
  }

  sortData() {
    this.filter();
  }

  filter(): void {
    this.queryParams = this.loanFilterService.filter(
      this.filters,
      this.paginator,
      this.sort
    );
    this.loanFacade
      .querySummary(this.queryParams)
      .pipe(untilDestroyed(this))
      .subscribe((data: LoanDataSummary) => {
        this.setLoanData(data);
        this.activeFilters = this.loanFilterService.addFilterParameters(
          this.queryParams,
          this.productCode,
          this.excludedProductCode
        );
      });
  }

  setLoanData(data: LoanDataSummary) {
    this.totalResultCount = data?.totalCount;
    let loans =
      this.productCode === ProductCode.Law
        ? data?.caseFinancingLoans
        : data?.otherLoans;

    if (!this.queryParams?.sort) {
      loans = [...loans]?.sort((l1, l2) =>
        this.loanFacade.loanSorter(l1, l2, this.currentAccount)
      );
    }

    this.loans = loans as (LoanData & CaseFinancingLoanData)[];
  }

  search(selectedLoan: LoanData) {
    if (selectedLoan) {
      this.filters.description = selectedLoan.description;
      this.filters.loanNumber = selectedLoan.loanNumber;
      this.filter();
    } else {
      const filterParamsToRemove = [
        { id: "description" },
        { id: "loanNumber" },
      ];
      this.activeFilterRemoved(filterParamsToRemove, true);
    }
  }

  download(params: any) {
    const queryParams = Object.assign({}, params);
    queryParams.entityId = this.entity.id;

    this.loanFacade.download(queryParams);
  }

  activeFilterRemoved(
    activefilters: FilterParameter[],
    clearAccountSearch: boolean
  ): void {
    if (!activefilters.length) return;
    activefilters.forEach((item) => {
      this.filters.remove(item.id);
      if (clearAccountSearch) this.accountSearchInputService.clearSearch.emit();
      this.filter();
    });
  }

  draw(loan: LoanData) {
    if(this.needsAttentionModel?.isApplicable) {
      NeedsAttentionHelper.openNeedsAttentionDialog(this.dialog, this.needsAttentionModel.messageInput, loan.productCode);
      return;
    }
    this.router.navigate([`/account/${loan.id}/transfer/draw`]);
  }

  pay(loan: LoanData) {
    this.router.navigate([`/account/${loan.id}/transfer/payment`]);
  }

  details(loan: LoanData): void {
    if (loan) {
      const hasCurrentLoanPending = LoanHelper.isLoanPendingForSign(loan.id, this.currentAccount);

      if (this.checkoutState?.identityVerificationAction !== IdentityVerificationAction.Skip)
        this.router.navigate([`account/${loan.id}/setup`]);
      else if (hasCurrentLoanPending)
        this.router.navigate([`account/${loan.id}/setup/sign-agreement`]);
      else if (loan.isNPLLoan)
        this.router.navigate([`/account/${loan.id}/bank-account`]);
      else this.router.navigate([`/account/${loan.id}/transactions`]);
    }
  }

  isLoanFavorite(loan: LoanData): boolean {
    return _.find(loan?.owners, { customerId: this.currentAccount.id })?.favorite;
  }

  filterDialog() {
    AccountListFilterDialogComponent.show(
      this.dialog,
      this.filters,
      this.productCode
    )
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        if (result) {
          this.filters = result;
          this.paginator.firstPage();
          this.filter();
        }
      });
  }

  updateFavorite(loanId: number, favorite: boolean) {
    this.loanFacade
      .updateFavorite(this.currentAccount.id, loanId, favorite)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.filter();
      });
  }

  closeSearchLoans() {
    this.showAccountsFilter = false;
  }

  getDisplayedColumns(): string[] {
    return this.displayedColumns
      .filter(
        (cd) =>
          (this.productCode && cd.showForProductCode == this.productCode) ||
          cd.showForProductCode == undefined
      )
      .map((cd) => cd.columnName);
  }
}
